import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { trpcClient } from '@framework/trpcClient';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';

/**
 * Firebase Auth の linkWithRedirect(Microsoft アカウント連携)からの復帰を検知して処理する常駐コンポーネント。
 * ログイン中(認証済み)のときに RootLayout 直下にマウントされる。
 *
 * linkWithRedirect は連携を開始したページの URL に戻ってくるため、特定のコールバックページではなく
 * アプリ共通の場所で getRedirectResult による復帰検知を行う。復帰時はバックエンドの verifyMicrosoftLink で
 * メールアドレス一致を検証し(不一致の場合はクライアント側で unlink して連携を巻き戻す)、結果を
 * AccountConnectedPage に表示する。
 *
 * Google / Microsoft それぞれのハンドラが同一の getRedirectResult() を呼ぶため、
 * credential?.providerId で自分が担当するリダイレクト結果かを判定してから処理する。
 */
export const MicrosoftLinkRedirectResultHandler: React.FC<unknown> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // StrictMode 等での effect 二重実行により復帰処理が多重に走らないようにする
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const navigateToResult = (isSuccess: boolean, message: string) => {
      navigate(UserPagePathBuilder.accountConnectedPage(), {
        state: { accountConnectedPage: { isSuccess, message } },
      });
    };

    FirebaseAuthenticationClient.getRedirectResult()
      .then(async (redirectResult) => {
        // リダイレクト復帰でない通常のページロード、または連携(link)以外の復帰(ログイン等)は対象外
        if (!redirectResult.user || redirectResult.operationType !== 'link') return;
        // Microsoft 以外のプロバイダー(Google 等)の連携復帰はこのハンドラでは処理しない
        // providerId が判別できた場合のみ除外する(undefined の場合は処理継続)
        const providerId = redirectResult.credential?.providerId ?? redirectResult.additionalUserInfo?.providerId;
        if (providerId !== undefined && providerId !== 'microsoft.com') return;

        try {
          const { result } = await trpcClient.user.auth.verifyMicrosoftLink.mutate();

          if (result === 'success') {
            navigateToResult(true, t('auth.microsoftLink.result.success'));
          } else if (result === 'mail-address-mismatch') {
            // メール不一致の連携はクライアント側で巻き戻す。
            // (Admin SDK での unlink はトークン失効で強制サインアウトになるためサーバー側では行わない)
            // 巻き戻しに失敗した場合は連携が残ったままのため、外側の catch で汎用エラーの結果画面に遷移させる
            await FirebaseAuthenticationClient.unlinkMicrosoft();
            navigateToResult(false, t('auth.microsoftLink.result.mailAddressMismatch'));
          } else if (result === 'not-linked') {
            // providerId が不明な場合は他プロバイダの redirectResult を誤って処理した可能性があるため無視する
            if (providerId !== 'microsoft.com') return;
            navigateToResult(false, t('auth.microsoftLink.result.error'));
          } else {
            navigateToResult(false, t('auth.microsoftLink.result.error'));
          }
        } catch (error) {
          console.error(error);
          navigateToResult(false, t('auth.microsoftLink.result.error'));
        }
      })
      .catch((error: { code?: string; credential?: { providerId?: string } }) => {
        // linkWithRedirect の失敗は復帰後の getRedirectResult の reject として通知される
        // Google と Microsoft のハンドラが同一の getRedirectResult() を参照するため、
        // credential.providerId で自分が担当するエラーかを判定してから処理する
        if (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use') {
          const providerId = error.credential?.providerId;
          if (providerId !== undefined && providerId !== 'microsoft.com') return;
          // 対象の Microsoft アカウントが既に他の Firebase ユーザーに連携されているケース
          navigateToResult(false, t('auth.microsoftLink.result.credentialAlreadyInUse'));
        } else if (error.code === 'auth/provider-already-linked') {
          const providerId = error.credential?.providerId;
          if (providerId !== undefined && providerId !== 'microsoft.com') return;
          navigateToResult(false, t('auth.microsoftLink.result.alreadyLinked'));
        } else {
          // 連携以外のリダイレクト失敗(ログイン等)の可能性があるため、ここでは結果画面へは遷移しない
          console.error(error);
        }
      });
  }, [navigate, t]);

  return null;
};
